/**
 * MetaConsentBar.jsx
 *
 * Bottom-fixed, minimalist consent bar for Meta Pixel. Shows only if the user
 * has NOT yet chosen. Two buttons:
 *   - "Akceptuję" / "Accept"  → localStorage flag + load pixel + fire pending PageView
 *   - "Odrzucam"  / "Reject"  → localStorage flag; pixel never loads
 *
 * The "Zobacz szczegóły" / "See details" link toggles a short in-bar summary of
 * what Meta Pixel is used for. The full Privacy Policy is available on
 * /pricing (collapsed by default there).
 */

import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getConsent, setConsent, loadPixel, mpTrack } from "@/lib/metaPixel";

export default function MetaConsentBar() {
  const { lang } = useAuth();
  const isEN = lang === "en";
  const [choice, setChoice] = useState(() => getConsent()); // null | 'accepted' | 'rejected'
  const [showDetails, setShowDetails] = useState(false);

  if (choice) return null; // user already decided → don't show

  const accept = () => {
    setConsent("accepted");
    loadPixel();
    // Fire the PageView the user "missed" while sitting on the consent bar.
    mpTrack("PageView");
    setChoice("accepted");
  };
  const reject = () => {
    setConsent("rejected");
    setChoice("rejected");
  };

  const label = {
    text: isEN
      ? "We use optional analytics and marketing technologies (Meta Pixel) to measure ad performance."
      : "Używamy opcjonalnych technologii analitycznych i marketingowych (Meta Pixel), aby mierzyć skuteczność reklam.",
    seeDetails:  isEN ? "See details"  : "Zobacz szczegóły",
    hideDetails: isEN ? "Hide details" : "Ukryj szczegóły",
    accept: isEN ? "Accept"  : "Akceptuję",
    reject: isEN ? "Reject"  : "Odrzucam",
    detailsBody: isEN ? (
      <>
        <p style={{ margin: "0 0 8px 0" }}>
          The Meta Pixel is a small piece of JavaScript that reports 3 anonymous
          events to Meta (Facebook): <em>PageView</em>, <em>CompleteRegistration</em> (when
          you create an account), and <em>Purchase</em> (with the amount + currency of a
          successful subscription — no personal or payment details).
        </p>
        <p style={{ margin: "0 0 8px 0" }}>
          <strong>We never send</strong> scene text, chats with Leo, CRM contact notes,
          names of people in your life, or any acting materials to Meta.
        </p>
        <p style={{ margin: 0 }}>
          Clicking <strong>Reject</strong> means the pixel is never loaded in your
          browser. Clicking <strong>Accept</strong> loads it and enables the 3 events
          above. You can read the full Privacy Policy at the bottom of the Pricing page.
        </p>
      </>
    ) : (
      <>
        <p style={{ margin: "0 0 8px 0" }}>
          Meta Pixel to niewielki skrypt JavaScript, który przesyła do Meta (Facebooka)
          3 anonimowe zdarzenia: <em>PageView</em>, <em>CompleteRegistration</em> (przy
          założeniu konta) oraz <em>Purchase</em> (z kwotą i walutą udanej subskrypcji —
          bez żadnych danych osobowych ani danych płatniczych).
        </p>
        <p style={{ margin: "0 0 8px 0" }}>
          <strong>Nigdy nie wysyłamy</strong> do Meta treści scen, rozmów z Leo,
          notatek z CRM, imion bliskich osób ani żadnych materiałów aktorskich.
        </p>
        <p style={{ margin: 0 }}>
          Kliknięcie <strong>Odrzucam</strong> sprawia, że Pixel nie zostanie
          załadowany w Twojej przeglądarce. Kliknięcie <strong>Akceptuję</strong>
          włącza go i uruchamia 3 powyższe zdarzenia. Pełną Politykę Prywatności
          znajdziesz na dole strony Cennika.
        </p>
      </>
    ),
  };

  return (
    <div
      data-testid="meta-consent-bar"
      style={{
        position: "fixed",
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 60,
        maxWidth: 900,
        margin: "0 auto",
        background: "rgba(23,20,16,0.96)",
        border: "1px solid rgba(120,113,108,0.35)",
        borderRadius: 10,
        boxShadow: "0 20px 40px -20px rgba(0,0,0,0.7)",
        padding: "14px 18px",
        backdropFilter: "blur(10px)",
        color: "#e7e5e4",
        fontSize: 13.5,
        lineHeight: 1.55,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
        <div style={{ flex: "1 1 320px", minWidth: 220 }}>
          <span>{label.text}</span>{" "}
          <button
            data-testid="meta-consent-details-toggle"
            onClick={() => setShowDetails(v => !v)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: "#f59e0b",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {showDetails ? label.hideDetails : label.seeDetails}
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
          <button
            data-testid="meta-consent-reject"
            onClick={reject}
            style={{
              background: "transparent",
              color: "#d6d3d1",
              border: "1px solid rgba(120,113,108,0.5)",
              borderRadius: 999,
              padding: "8px 18px",
              cursor: "pointer",
              fontSize: 13,
              fontFamily: "inherit",
            }}
          >
            {label.reject}
          </button>
          <button
            data-testid="meta-consent-accept"
            onClick={accept}
            style={{
              background: "#f59e0b",
              color: "#0f0d0b",
              border: "none",
              borderRadius: 999,
              padding: "8px 20px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            {label.accept}
          </button>
        </div>
      </div>

      {showDetails && (
        <div
          data-testid="meta-consent-details-body"
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid rgba(120,113,108,0.25)",
            fontSize: 13,
            color: "#a8a29e",
            maxHeight: 220,
            overflow: "auto",
          }}
        >
          {label.detailsBody}
        </div>
      )}
    </div>
  );
}
